import React, { useState } from "react";
import {
  Wand2, X, Loader2, Sparkles, BookOpen, Lightbulb, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { improveThis, errorMessage } from "../../lib/api";

/** Improve This — Ultra Pro per-issue coach.
 * Renders as a small "Improve This ✨" button on each priority_improvement.
 * Clicking opens a modal that lazy-loads the coaching payload for THAT
 * specific issue and displays it as 4 clear sections: Why flagged →
 * Suggestion → Why better → Suggested evidence.
 *
 * Non-Ultra users see a locked "Coming with Ultra Pro" pill instead of the
 * live button.
 */
export default function ImproveThisButton({ analysisId, issue, isUltraPro }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  if (!isUltraPro) {
    return (
      <>
        <button
          onClick={() => setShowUpsell(true)}
          data-testid="improve-this-locked"
          className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-50 to-indigo-50 hover:from-amber-100 hover:to-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 transition-colors cursor-pointer"
          title="Unlock with Ultra Pro"
        >
          <Sparkles size={10} /> Unlock with Ultra Pro
        </button>
        {showUpsell && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ultra-pro-upsell-modal" onClick={() => setShowUpsell(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-br from-amber-500 via-indigo-600 to-indigo-800 text-white px-6 py-6 text-center">
                <Sparkles size={32} className="mx-auto mb-2" />
                <h3 className="font-display text-2xl font-semibold">Ultra Pro — in Beta</h3>
                <p className="text-sm text-indigo-100 mt-1">Complimentary for Pro subscribers while we refine it.</p>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">✓</div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">Improve This — one issue at a time</p>
                    <p className="text-xs text-zinc-600">See exactly why each flag was raised, get a targeted suggestion, and learn why the fix lifts your mark.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">✓</div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">Suggested evidence</p>
                    <p className="text-xs text-zinc-600">Example Harvard-style citations you can look up when your argument needs stronger backing.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">✓</div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">Priority processing & more</p>
                    <p className="text-xs text-zinc-600">Faster reports, Evidence Finder, and advanced lecturer feedback — all included in the Beta.</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 italic pt-2 border-t border-zinc-100">
                  You stay in control. MyAE never rewrites your work — it teaches you how.
                </p>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowUpsell(false)}
                    data-testid="ultra-pro-upsell-close"
                    className="flex-1 text-sm text-zinc-700 hover:text-zinc-900 border border-zinc-200 hover:bg-zinc-50 rounded-lg px-4 py-2 font-medium"
                  >
                    Maybe later
                  </button>
                  <a
                    href="/pricing"
                    data-testid="ultra-pro-upgrade-btn"
                    className="flex-1 text-center text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2 font-semibold"
                  >
                    Upgrade to Pro
                  </a>
                </div>
                <p className="text-[10px] text-zinc-400 text-center pt-1">
                  Ultra Pro is included with any active Pro subscription during Beta.
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const load = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const res = await improveThis(analysisId, {
        issue_text: issue.action || "",
        issue_class: issue.issue_class || "",
      });
      setData(res);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load Improve This coaching."));
      setOpen(false);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={load}
        data-testid="improve-this-btn"
        className="inline-flex items-center gap-1 rounded-full bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 transition-colors"
      >
        <Wand2 size={10} /> Improve this
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-auto p-4" data-testid="improve-this-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mt-12 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white px-6 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 size={16} />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">Improve This</span>
                  <span className="text-[10px] bg-white/20 rounded-full px-2 py-0.5">Ultra Pro</span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{issue.action}</h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="improve-this-close" className="text-indigo-200 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {loading && (
                <div className="flex items-center gap-3 text-sm text-zinc-700 py-8 justify-center" data-testid="improve-this-loading">
                  <Loader2 size={16} className="animate-spin text-indigo-600" />
                  Thinking through your options…
                </div>
              )}

              {!loading && data && (
                <>
                  {/* Why flagged */}
                  <div data-testid="improve-this-why-flagged">
                    <div className="flex items-center gap-2 mb-1.5">
                      <BookOpen size={13} className="text-amber-600" />
                      <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">Why this was flagged</p>
                    </div>
                    <p className="text-sm text-zinc-800 leading-relaxed">{data.why_flagged}</p>
                  </div>

                  {/* Suggestion */}
                  <div data-testid="improve-this-suggestion">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Lightbulb size={13} className="text-indigo-600" />
                      <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">Suggested improvement</p>
                    </div>
                    <div className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 px-4 py-3">
                      <p className="text-sm text-zinc-900 leading-relaxed">{data.suggestion}</p>
                    </div>
                  </div>

                  {/* Why better */}
                  <div data-testid="improve-this-why-better">
                    <div className="flex items-center gap-2 mb-1.5">
                      <TrendingUp size={13} className="text-emerald-600" />
                      <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">Why this is better</p>
                    </div>
                    <p className="text-sm text-zinc-800 leading-relaxed">{data.why_better}</p>
                  </div>

                  {/* Suggested evidence */}
                  {(data.suggested_evidence?.recommendation || data.suggested_evidence?.example_citation) && (
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4" data-testid="improve-this-evidence">
                      <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2">Suggested evidence</p>
                      {data.suggested_evidence.recommendation && (
                        <p className="text-sm text-zinc-800 leading-relaxed mb-2">{data.suggested_evidence.recommendation}</p>
                      )}
                      {data.suggested_evidence.source_type && (
                        <p className="text-xs text-zinc-600 mb-1"><span className="font-semibold">Source type:</span> {data.suggested_evidence.source_type}</p>
                      )}
                      {data.suggested_evidence.example_citation && (
                        <p className="text-xs text-zinc-600 italic"><span className="not-italic font-semibold">Example citation:</span> {data.suggested_evidence.example_citation}</p>
                      )}
                    </div>
                  )}

                  <p className="text-[11px] text-zinc-500 italic pt-1 border-t border-zinc-100">
                    You stay in control — MyAE never rewrites your work automatically. Try the suggestion in your own words.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
